import { Observable } from 'rxjs'
import { BaseCommand, CommandResult } from './base-command'

/**
 * Base class for non-visual, injectable commands (create/edit/delete data).
 *
 * Extends {@link BaseCommand} with the ergonomics those commands rely on:
 * - {@link setArgs} to stash arguments for a later argument-less `execute()`
 *   (used when the command is handed to a generic button via `[command]`).
 * - {@link isExecuting} / {@link result} convenience accessors for templates.
 *
 * The work itself is implemented in `doExecute` and returns an
 * `Observable<boolean>`, like every other command in the app.
 */
export abstract class CoreCommand<TArgs = void, TResult = unknown> extends BaseCommand<
  TArgs,
  TResult
> {
  protected args: TArgs | null = null

  /** Stash arguments for a later argument-less `execute()` call. */
  setArgs(args: TArgs): void {
    this.args = args
  }

  /** True while the command is running. Convenience for templates. */
  get isExecuting(): boolean {
    return this.status === 'running'
  }

  /** The last result produced. Convenience for templates and services. */
  get result(): CommandResult<TResult> | null {
    return this.lastResult
  }

  override canExecute(args?: TArgs): boolean {
    return super.canExecute(args ?? this.args ?? undefined)
  }

  override execute(args?: TArgs): Observable<boolean> {
    return super.execute(args ?? this.args ?? undefined)
  }
}
